import type { IFeatureFlags, OrderableSharingFlag } from 'data/tenant-config';
import type { ISessionData } from 'data/schema';

export type { OrderableSharingFlag } from 'data/tenant-config';

export interface SharingOptionDescriptor {
  flagKey: OrderableSharingFlag;
  sharingType: ISessionData['sharingType'];
  icon: string;
}

/**
 * Canonical mapping from feature flag keys to sharing types and UI metadata.
 * This is the single source of truth — the order here is the default display order.
 */
export const CANONICAL_SHARING_ORDER: readonly SharingOptionDescriptor[] = [
  { flagKey: 'sharingRemote', sharingType: 'remote', icon: 'ex3d-icon--video-on' },
  { flagKey: 'sharingBroadcast', sharingType: 'broadcast', icon: 'ex3d-icon--cast' },
  { flagKey: 'sharingBroadcastLocal', sharingType: 'broadcast-local', icon: 'ex3d-icon--display' },
  { flagKey: 'sharingSameDevice', sharingType: 'device', icon: 'ex3d-icon--display' },
] as const;

const FLAG_TO_DESCRIPTOR = new Map(CANONICAL_SHARING_ORDER.map((d) => [d.flagKey, d]));

/**
 * The set of sharing types that are "orderable" — i.e. managed by sharingOptionsOrder.
 * Non-orderable types like 'scheduled' and 'dga' are user selections that should be preserved.
 */
export const ORDERABLE_SHARING_TYPES: ReadonlySet<ISessionData['sharingType']> = new Set(
  CANONICAL_SHARING_ORDER.map((d) => d.sharingType)
);

type SharingFlags = Pick<IFeatureFlags, OrderableSharingFlag>;

/**
 * Returns enabled sharing options in the configured order.
 * - Custom order entries come first (disabled ones filtered out).
 * - Enabled options not in the custom order are appended in canonical order.
 * - Returns [] when no options are enabled.
 */
export function getOrderedSharingOptions(
  flags: SharingFlags,
  sharingOptionsOrder: OrderableSharingFlag[] | undefined
): SharingOptionDescriptor[] {
  const order = sharingOptionsOrder?.length ? sharingOptionsOrder : CANONICAL_SHARING_ORDER.map((d) => d.flagKey);
  const seen = new Set<OrderableSharingFlag>();
  const result: SharingOptionDescriptor[] = [];

  // Add explicitly ordered options (if enabled)
  for (const flagKey of order) {
    if (seen.has(flagKey)) continue;
    seen.add(flagKey);
    const descriptor = FLAG_TO_DESCRIPTOR.get(flagKey);
    if (descriptor && flags[flagKey]) {
      result.push(descriptor);
    }
  }

  // Append unlisted-but-enabled options in canonical order
  for (const descriptor of CANONICAL_SHARING_ORDER) {
    if (!seen.has(descriptor.flagKey) && flags[descriptor.flagKey]) {
      result.push(descriptor);
    }
  }

  return result;
}

/**
 * Returns the sharing type of the first enabled option in the configured order.
 * Returns null when no sharing options are enabled.
 */
export function getDefaultSharingType(
  flags: SharingFlags,
  sharingOptionsOrder: OrderableSharingFlag[] | undefined
): ISessionData['sharingType'] | null {
  const ordered = getOrderedSharingOptions(flags, sharingOptionsOrder);
  return ordered.length > 0 ? ordered[0].sharingType : null;
}

/**
 * Resolves which sharing type should be active given the current session state and config.
 *
 * Rules:
 * - Shared + scheduled → 'remote' (if enabled), otherwise defaultType
 * - Shared + orderable type → keep it (if enabled), otherwise defaultType
 * - Shared + non-orderable type (dga) → keep it
 * - Not shared + non-orderable type (scheduled, dga) → keep it
 * - Not shared + orderable type → reset to defaultType (respects custom order)
 */
export function resolveSharingType(
  currentType: ISessionData['sharingType'] | undefined,
  isShared: boolean,
  defaultType: ISessionData['sharingType'] | null,
  enabledOrderableTypes: ReadonlySet<ISessionData['sharingType']>
): ISessionData['sharingType'] | null {
  if (isShared) {
    // Scheduled sessions default to remote
    if (currentType === 'scheduled') {
      return enabledOrderableTypes.has('remote') ? 'remote' : defaultType;
    }
    // Non-orderable types (dga) are preserved as-is
    if (currentType && !ORDERABLE_SHARING_TYPES.has(currentType)) {
      return currentType;
    }
    // Orderable types: validate that the type is still enabled
    return currentType && enabledOrderableTypes.has(currentType) ? currentType : defaultType;
  }

  // Not shared: preserve non-orderable user selections (scheduled, dga)
  if (currentType && !ORDERABLE_SHARING_TYPES.has(currentType)) {
    return currentType;
  }
  // Reset orderable types to configured default (respects custom sharingOptionsOrder)
  return defaultType;
}

/**
 * AVS-4354: Car Comparison is a 2D side-by-side view, and the local renderer (LocalAVE) can
 * only display a 3D stream (AVS-4335, see `is2DBlocked`). Neither visualisation is therefore
 * possible from Comparison, so the option must be neither selectable nor startable there —
 * mirroring the 3D lockout already applied to the experience selector.
 */
export function isSharingOptionBlockedByComparison(
  sharingType: ISessionData['sharingType'] | undefined,
  comparisonMode: ISessionData['comparisonMode'] | undefined
): boolean {
  return sharingType === 'broadcast-local' && comparisonMode === 'on';
}

/**
 * Resolves which sharing type the SharingMenu should display.
 *
 * Returns null in two cases:
 *  - Preserve: session is not shared and the current orderable selection is still enabled
 *    (caller should leave the active type unchanged).
 *  - No options: no default type and no enabled orderable types exist.
 */
export function resolveActiveMenuType(
  flags: SharingFlags,
  sharingOptionsOrder: OrderableSharingFlag[] | undefined,
  currentType: ISessionData['sharingType'] | undefined,
  isShared: boolean
): ISessionData['sharingType'] | null {
  const defaultType = getDefaultSharingType(flags, sharingOptionsOrder);
  const enabledTypes = new Set(getOrderedSharingOptions(flags, sharingOptionsOrder).map((o) => o.sharingType));

  // Preserve the user's explicit selection when the session ends (isShared becomes false).
  // Only reset to the configured default if there is no current selection or it has become disabled.
  if (!isShared && currentType && enabledTypes.has(currentType)) {
    return null;
  }

  return resolveSharingType(currentType, isShared, defaultType, enabledTypes);
}
